import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Card,
    Chip,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    CloudOff as OfflineIcon,
    Sync as SyncIcon,
    Lock as ViewOnlyIcon,
} from '@mui/icons-material';
import safetyScheduleService from '../../services/safetyScheduleService';
import {
    DOW,
    pad,
    parseDay,
    todayKey,
    weekDays,
    weekEndingOf,
    weekLabel,
    orderWeeks,
    weekFromQuery,
} from './utils/scheduleDates';
// «Burgess, Christopher Glenn» → фамилия отдельной строкой, имя под ней: в узкой
// колонке дня полное имя одной строкой рвётся где попало.
import { peopleInCell, splitName } from './schedule/visitUtils';

const BORDER = '1px solid #e0e0e0';
// Пустые выходные сжимаются до узкой колонки: на площадках по субботам обычно
// никого, и две пустые колонки во всю ширину отнимали бы место у будних дней.
const COLLAPSED_WEEKEND_WIDTH = 56;

/** Когда расписание в последний раз приехало на планшет — по Каролине. */
function lastSyncLabel() {
    try {
        const raw = localStorage.getItem('lastSafetyScheduleSyncTime');
        if (!raw) return null;
        const date = new Date(raw);
        if (Number.isNaN(date.getTime())) return null;
        return date.toLocaleString('en-US', {
            timeZone: 'America/New_York',
            month: '2-digit',
            day: '2-digit',
            hour: 'numeric',
            minute: '2-digit',
        });
    } catch (e) {
        return null;
    }
}

/**
 * Group View: кто из команды куда едет на неделе — та же сетка, что в админке
 * (Safety Scheduling), но только для просмотра.
 *
 * Данные читаются из IndexedDB: расписание команды приезжает тем же синком, что
 * и My Schedule, поэтому экран открывается и без сети.
 */
const SafetyScheduleGroupView = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [teamWeeks, setTeamWeeks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [online, setOnline] = useState(navigator.onLine);

    const today = useMemo(() => todayKey(), []);
    const currentWeek = useMemo(() => weekEndingOf(today), [today]);
    const selectedWeek = weekFromQuery(searchParams.get('week')) || currentWeek;

    const load = useCallback(async () => {
        setTeamWeeks(await safetyScheduleService.getTeamWeeks());
        setLoading(false);
    }, []);

    // Свежее тянем сами при открытии, как и My Schedule: на экран пришли ради
    // расписания, ждать ручной синхронизации незачем.
    const refresh = useCallback(async () => {
        setRefreshing(true);
        await safetyScheduleService.refreshFromServer();
        await load();
        setRefreshing(false);
    }, [load]);

    useEffect(() => {
        load();
        refresh();

        const goOnline = () => { setOnline(true); refresh(); };
        const goOffline = () => setOnline(false);

        window.addEventListener('online', goOnline);
        window.addEventListener('offline', goOffline);
        return () => {
            window.removeEventListener('online', goOnline);
            window.removeEventListener('offline', goOffline);
        };
    }, [load, refresh]);

    const weekOptions = useMemo(
        () => orderWeeks([...teamWeeks.map((w) => w.weekEnding), currentWeek, selectedWeek], currentWeek),
        [teamWeeks, currentWeek, selectedWeek]
    );

    const week = useMemo(
        () => teamWeeks.find((w) => w.weekEnding === selectedWeek) || null,
        [teamWeeks, selectedWeek]
    );

    const days = useMemo(
        () => (week?.days?.length === 7 ? week.days : weekDays(selectedWeek)),
        [week, selectedWeek]
    );

    const rows = useMemo(() => week?.rows || [], [week]);

    // Суббота и воскресенье занимают полную колонку, только если на этот день
    // кто-то стоит, — каждый день сам по себе: субботняя смена на одной площадке
    // не повод растягивать пустое воскресенье.
    const collapsed = useMemo(
        () => days.map((date, index) => index >= 5 && !rows.some((row) => peopleInCell(row, date).length > 0)),
        [rows, days]
    );

    const scheduledCount = useMemo(() => {
        const ids = new Set();
        rows.forEach((row) => days.forEach((date) => peopleInCell(row, date)
            .forEach((person) => ids.add(person.repId ?? person.name))));
        return ids.size;
    }, [rows, days]);

    const selectWeek = (value) => {
        setSearchParams(value === currentWeek ? {} : { week: value }, { replace: true });
    };

    const goBack = () => {
        navigate(selectedWeek === currentWeek ? '/safety/schedule' : `/safety/schedule?week=${selectedWeek}`);
    };

    const syncedAt = lastSyncLabel();
    const viewerRepId = week?.viewerRepId ?? null;

    const renderDayHeader = (date, index) => {
        const d = parseDay(date);
        const isToday = date === today;
        const isWeekend = index >= 5;
        const narrow = collapsed[index];

        return (
            <Box
                component="th"
                key={date}
                scope="col"
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    px: narrow ? 0.25 : 0.75,
                    py: 0.75,
                    textAlign: 'center',
                    bgcolor: isToday ? '#e8f5e9' : (isWeekend ? '#eeeeee' : 'grey.100'),
                    borderRight: index === 6 ? 'none' : BORDER,
                    borderBottom: isToday ? '3px solid #4caf50' : '2px solid #d0d0d0',
                    lineHeight: 1.2,
                }}
            >
                <Box sx={{
                    fontSize: narrow ? '0.72rem' : '0.82rem',
                    fontWeight: 700,
                    color: isToday ? '#2e7d32' : 'text.primary',
                }}>
                    {DOW[d.getDay()]}
                </Box>
                <Box sx={{
                    fontSize: narrow ? '0.62rem' : '0.72rem',
                    fontWeight: isToday ? 700 : 500,
                    color: isToday ? '#2e7d32' : 'text.secondary',
                }}>
                    {pad(d.getMonth() + 1)}/{pad(d.getDate())}
                </Box>
            </Box>
        );
    };

    const renderSlot = (row, date, index) => {
        // В ячейке может стоять несколько человек — каждый своей строкой.
        const people = peopleInCell(row, date);
        const isToday = date === today;
        const isWeekend = index >= 5;
        const isMine = (person) => viewerRepId !== null && person.repId === viewerRepId;
        const mine = people.some(isMine);

        let background = 'white';
        if (isWeekend) background = '#fafafa';
        if (isToday) background = '#f1f8e9';
        if (mine) background = '#e3f2fd';

        return (
            <Box
                component="td"
                key={date}
                sx={{
                    px: 0.75,
                    py: 0.75,
                    bgcolor: background,
                    borderRight: index === 6 ? 'none' : BORDER,
                    borderBottom: BORDER,
                    verticalAlign: people.length > 1 ? 'top' : 'middle',
                    // Полоса слева — чтобы свои дни находились глазом и на
                    // сегодняшней зелёной колонке, где голубой фон теряется.
                    boxShadow: mine ? 'inset 3px 0 0 #1976d2' : 'none',
                    overflowWrap: 'anywhere',
                }}
            >
                {people.length ? people.map((person, i) => {
                    const me = isMine(person);
                    const { last, first } = splitName(person.name);
                    return (
                        <Box
                            key={`${person.repId ?? person.name}-${i}`}
                            sx={{
                                // Между людьми одной ячейки — тонкая черта, чтобы
                                // имя одного не читалось как продолжение другого.
                                ...(i > 0 ? { mt: 0.5, pt: 0.5, borderTop: '1px dashed #d0d7de' } : {}),
                            }}
                        >
                            <Box sx={{
                                fontSize: '0.8rem',
                                fontWeight: me ? 700 : 600,
                                lineHeight: 1.25,
                                color: me ? '#0d47a1' : 'text.primary',
                            }}>
                                {last}
                            </Box>
                            {first && (
                                <Box sx={{
                                    fontSize: '0.7rem',
                                    lineHeight: 1.25,
                                    color: me ? '#1565c0' : 'text.secondary',
                                }}>
                                    {first}
                                </Box>
                            )}
                        </Box>
                    );
                }) : (
                    <Box sx={{ textAlign: 'center', color: 'text.disabled', fontSize: '0.75rem' }}>—</Box>
                )}
            </Box>
        );
    };

    const renderGrid = () => (
        <Paper
            elevation={1}
            sx={{
                borderRadius: 2,
                // Сетка сжимается под экран и прокручивается сама, шапка дней при
                // этом стоит на месте. Растягивать её на всю высоту незачем.
                flex: '0 1 auto',
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                WebkitOverflowScrolling: 'touch',
            }}
        >
            <table
                aria-label="Team schedule: locations by day"
                style={{
                    width: '100%',
                    // Фиксированная раскладка: ширина таблицы = ширина экрана,
                    // длинное название или имя переносится, а не раздвигает сетку
                    // в горизонтальный скролл.
                    tableLayout: 'fixed',
                    borderCollapse: 'separate',
                    borderSpacing: 0,
                }}
            >
                <colgroup>
                    <col style={{ width: '22%' }} />
                    {days.map((date, index) => (
                        <col
                            key={date}
                            style={collapsed[index] ? { width: COLLAPSED_WEEKEND_WIDTH } : undefined}
                        />
                    ))}
                </colgroup>
                <thead>
                    <tr>
                        <Box
                            component="th"
                            scope="col"
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 2,
                                px: 1,
                                py: 0.75,
                                textAlign: 'left',
                                fontSize: '0.82rem',
                                fontWeight: 700,
                                bgcolor: 'grey.100',
                                borderRight: BORDER,
                                borderBottom: '2px solid #d0d0d0',
                            }}
                        >
                            Location
                        </Box>
                        {days.map(renderDayHeader)}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.id}>
                            <Box
                                component="th"
                                scope="row"
                                sx={{
                                    px: 1,
                                    py: 0.75,
                                    textAlign: 'left',
                                    fontWeight: 'normal',
                                    bgcolor: 'white',
                                    borderRight: BORDER,
                                    borderBottom: BORDER,
                                    verticalAlign: 'middle',
                                    overflowWrap: 'anywhere',
                                }}
                            >
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.3 }}>
                                    {row.location}
                                </Typography>
                                {row.notes && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', lineHeight: 1.3, mt: 0.25, whiteSpace: 'pre-line' }}
                                    >
                                        {row.notes}
                                    </Typography>
                                )}
                            </Box>
                            {days.map((date, index) => renderSlot(row, date, index))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </Paper>
    );

    const renderContent = () => {
        if (loading || (refreshing && !week)) {
            return (
                <Card sx={{ p: 4 }}>
                    <Typography>Loading...</Typography>
                </Card>
            );
        }

        // Недели нет на планшете совсем — это не «пустая неделя»: расписание
        // сюда ещё не приезжало, и говорить «никого не поставили» было бы враньём.
        if (!week) {
            return (
                <Card sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary">
                        The team schedule is not on this iPad yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {online
                            ? 'Run Synchronization, then open Group View again.'
                            : 'No connection. Connect to the internet and run Synchronization.'}
                    </Typography>
                </Card>
            );
        }

        return (
            <>
                {!online && (
                    <Card sx={{ p: 1.5, backgroundColor: '#fff8e1', flexShrink: 0 }}>
                        <Typography variant="body2" color="text.secondary">
                            No connection. {syncedAt
                                ? `This is the schedule as of ${syncedAt}.`
                                : 'This is what was on the iPad last time.'}
                        </Typography>
                    </Card>
                )}

                {rows.length === 0 ? (
                    <Card sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary">Nothing scheduled this week</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Nobody has been put on a site for these days yet.
                        </Typography>
                    </Card>
                ) : (
                    <>
                        <Card sx={{ p: 1.5, flexShrink: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="subtitle2" fontWeight={600}>
                                    {rows.length} {rows.length === 1 ? 'location' : 'locations'}
                                    {' · '}
                                    {scheduledCount} {scheduledCount === 1 ? 'person' : 'people'} scheduled
                                </Typography>
                                <Box sx={{ flexGrow: 1 }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                                    <ViewOnlyIcon sx={{ fontSize: 16 }} />
                                    <Typography variant="caption">View only — the office sets the schedule</Typography>
                                </Box>
                            </Box>

                            {/* Кого на этой неделе никуда не поставили — та же полоска,
                                что над сеткой в админке. */}
                            {week.unassigned?.length > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                        Not scheduled this week:
                                    </Typography>
                                    {week.unassigned.map((name) => (
                                        <Chip
                                            key={name}
                                            label={name}
                                            size="small"
                                            variant="outlined"
                                            sx={{ height: 22, borderColor: '#ffcc80', backgroundColor: '#fff8e1', color: '#8a5300' }}
                                        />
                                    ))}
                                </Box>
                            )}
                        </Card>

                        {renderGrid()}
                    </>
                )}
            </>
        );
    };

    return (
        <Box sx={{
            height: 'calc(100vh - 60px)',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
        }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <IconButton onClick={goBack} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight={600} noWrap>Group View</Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            select
                            size="small"
                            value={selectedWeek}
                            onChange={(e) => selectWeek(e.target.value)}
                            sx={{ minWidth: 190 }}
                        >
                            {weekOptions.map((w) => (
                                <MenuItem key={w} value={w}>{weekLabel(w, currentWeek)}</MenuItem>
                            ))}
                        </TextField>
                        {!online && (
                            <Chip
                                icon={<OfflineIcon sx={{ color: 'white !important' }} />}
                                label="Offline"
                                size="small"
                                sx={{ backgroundColor: '#ff9800', color: 'white', height: 24 }}
                            />
                        )}
                        {refreshing && (
                            <Chip
                                icon={<SyncIcon sx={{ color: 'white !important' }} />}
                                label="Updating"
                                size="small"
                                sx={{ backgroundColor: '#9e9e9e', color: 'white', height: 24 }}
                            />
                        )}
                    </Stack>
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{
                p: 1.5,
                flexGrow: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                overflow: 'hidden',
            }}>
                {renderContent()}
            </Box>
        </Box>
    );
};

export default SafetyScheduleGroupView;
